import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { Chip, ChoiceChips, READ_TYPES, ToggleChips } from '../ui/bits';
import type { BookEdits } from './actions';
import type { NotForMeKind } from './changes';
import { copiesInOrder, copiesInUse, copyName, editionOf, isListen, latestProgress, openRead, readableNow, readsInOrder, statusName, timeText, triesSinceFinish } from './model';
import type { BookData, Read, Status } from './types';

// Book page Part 3, reading (Phase 6.1; agreed with Kristi Oct 27; decisions doc › "Book page
// Part 3"): the sheets for Start reading, Stop reading and Mark finished, where a book goes on a
// shelf, the progress slider, stars, the pause reason, and + Reading note / + Add a quote.

/** A sheet over the page: in the middle on a laptop, from the bottom on a phone. */
export function Sheet({ title, sub, onClose, back, children }: { title: string; sub?: string; onClose: () => void; back?: ComponentChildren; children: ComponentChildren }) {
  return (
    <>
      <button type="button" class="sheet-scrim" aria-label="Cancel" tabIndex={-1} onClick={onClose} />
      <div class="bk-dialog" role="dialog" aria-modal="true" aria-label={title} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        {/* A step inside the sheet: the one back link, top left above the heading (R8 W5). */}
        {back}
        <h3 class="bk-dialog-title">{title}</h3>
        {sub ? <p class="bk-dialog-sub">{sub}</p> : null}
        {children}
      </div>
    </>
  );
}

// ---------- where on a shelf ----------

export type Place = { place: 'kept' | 'top' | 'bottom' | string };

/** Kept place (or the bottom) · Top · Bottom · #n (Kristi, Oct 27). */
export function PlaceChoice({ shelf, kept, value, onChange, preferTop }: { shelf: Status; kept: boolean; value: Place; onChange: (p: Place) => void; preferTop?: boolean }) {
  const [n, setN] = useState('');
  const options: { key: string; label: string }[] = [
    ...(kept && !preferTop ? [{ key: 'kept', label: 'Its old place' }] : []),
    { key: 'top', label: 'Top' },
    { key: 'bottom', label: 'Bottom' },
  ];
  const isNumber = /^\d+$/.test(value.place);
  return (
    <fieldset class="bk-place">
      <legend class="bk-label">Where on {statusName(shelf)}</legend>
      <div class="bk-dialog-chips">
        {options.map((o) => (
          <Chip key={o.key} label={o.label} on={value.place === o.key} onToggle={() => onChange({ place: o.key })} />
        ))}
        <label class={`chip bk-place-n${isNumber ? ' on' : ''}`}>
          #
          <input
            type="number"
            min={1}
            inputMode="numeric"
            aria-label="Place number"
            value={isNumber ? value.place : n}
            onInput={(e) => {
              const v = (e.target as HTMLInputElement).value;
              setN(v);
              if (/^\d+$/.test(v) && Number(v) > 0) onChange({ place: v });
            }}
          />
        </label>
      </div>
    </fieldset>
  );
}

/** The default place on a shelf: its kept place when it has one, else the bottom. */
export const defaultPlace = (book: BookData, shelf: Status): Place => ({ place: book.keptShelves.includes(shelf) ? 'kept' : 'bottom' });

// ---------- Start reading ----------

const stoppedSinceFinish = (book: BookData): Read | undefined => {
  const ordered = readsInOrder(book.reads);
  const last = ordered.at(-1);
  return last?.outcome === 'stopped' ? last : undefined;
};

/** Start reading · Start a re-read… · Start again… (one sheet; it asks only what it needs). */
export function StartSheet({ book, edits, onClose }: { book: BookData; edits: BookEdits; onClose: () => void }) {
  const readable = copiesInOrder(book).filter(readableNow);
  // Every copy you can read now is ticked to start (Kristi, Oct 27); untick what you're not using.
  const [ticked, setTicked] = useState<string[]>(readable.map((c) => c.id));
  const stopped = stoppedSinceFinish(book);
  const [pickUp, setPickUp] = useState(Boolean(stopped?.stopped_percent));
  const names = book.readTypes.length ? book.readTypes.map((t) => t.name) : READ_TYPES.map((t) => t.name);
  const [readType, setReadType] = useState<string | null>(null);
  const [place, setPlace] = useState<Place>({ place: 'top' });
  const [busy, setBusy] = useState(false);
  const rereading = book.reads.some((r) => r.outcome === 'finished');
  const title = rereading ? `Start a re-read of ${book.work.title}` : stopped ? `Start ${book.work.title} again` : `Start reading ${book.work.title}`;
  const go = async () => {
    setBusy(true);
    const ok = await edits.act(
      'read_start',
      { copies: ticked, pick_up: pickUp, read_type: book.readType ? null : readType, place },
      rereading ? `Started a re-read of ${book.work.title}` : `Started ${book.work.title}`,
    );
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet title={title} sub="It goes on Currently Reading, started today." onClose={onClose}>
      {readable.length > 1 ? (
        <fieldset class="bk-place">
          <legend class="bk-label">Which copy?</legend>
          {readable.map((c) => (
            <label key={c.id} class="bk-check">
              <input
                type="checkbox"
                checked={ticked.includes(c.id)}
                onChange={(e) => setTicked((e.target as HTMLInputElement).checked ? [...ticked, c.id] : ticked.filter((x) => x !== c.id))}
              />
              {copyName(book, c)}
            </label>
          ))}
        </fieldset>
      ) : null}
      {stopped?.stopped_percent ? (
        <div class="bk-dialog-chips" role="group" aria-label="Where to start">
          <Chip label={`Pick up at ${Math.round(stopped.stopped_percent)}%`} on={pickUp} onToggle={() => setPickUp(true)} />
          <Chip label="Start over" on={!pickUp} onToggle={() => setPickUp(false)} />
        </div>
      ) : null}
      {!book.readType ? (
        <fieldset class="bk-place">
          <legend class="bk-label">Read type (optional)</legend>
          <div class="bk-dialog-chips">
            {names.map((n) => (
              <Chip key={n} label={n} on={readType === n} onToggle={() => setReadType(readType === n ? null : n)} />
            ))}
          </div>
        </fieldset>
      ) : null}
      <PlaceChoice shelf="currently_reading" kept={false} value={place} onChange={setPlace} preferTop />
      <div class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy || (readable.length > 1 && !ticked.length)} onClick={() => void go()}>
          {rereading ? 'Start the re-read' : 'Start reading'}
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

// ---------- Stop reading ----------

export type StopChoice = 'future_reads' | 'up_next' | 'not_for_me';

const STOP_CHIPS = ['Not in the mood for this kind', 'Too long for now', 'Too heavy right now', 'Lost interest', 'Not what I expected'];

/**
 * Stop reading…; with `loanCopy` it's Didn't finish… on a library copy (Part 4): the loan is
 * returned first, with an extra first choice, Keep reading · paused for now, and a Place a new
 * hold tick box.
 */
export function StopSheet({
  book,
  edits,
  onClose,
  initial = 'future_reads',
  loanCopy,
}: {
  book: BookData;
  edits: BookEdits;
  onClose: () => void;
  initial?: StopChoice;
  loanCopy?: string;
}) {
  const [choice, setChoice] = useState<StopChoice | 'keep_paused'>(loanCopy ? 'keep_paused' : initial);
  const [hold, setHold] = useState(false);
  const [kind, setKind] = useState<NotForMeKind>('abandoned');
  const [chips, setChips] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [place, setPlace] = useState<Place>(defaultPlace(book, initial === 'up_next' ? 'up_next' : 'future_reads'));
  const [busy, setBusy] = useState(false);
  const pct = latestProgress(openRead(book));
  const { tries } = triesSinceFinish(book.reads);
  const pick = (c: StopChoice | 'keep_paused') => {
    setChoice(c);
    if (c === 'future_reads' || c === 'up_next') setPlace(defaultPlace(book, c));
  };
  const choices: { key: StopChoice | 'keep_paused'; name: string; note: string }[] = [
    ...(loanCopy ? [{ key: 'keep_paused' as const, name: 'Keep reading · paused for now', note: 'The read stays open and paused, at your place, until you get a copy again.' }] : []),
    { key: 'future_reads', name: '↺ Try again later · Future reads', note: 'Marked Try again; it goes back to its old place in your reading order.' },
    { key: 'up_next', name: '↺ Try again · back to Up Next', note: 'Marked Try again; where you choose on Up Next.' },
    { key: 'not_for_me', name: 'Not for me', note: 'Moves to Not for me. You can bring it back any time.' },
  ];
  const save = async () => {
    setBusy(true);
    if (loanCopy) {
      const back = await edits.act('loan_step', { copy: loanCopy, step: 'returned', details: { didnt_finish: true, percent: pct, new_hold: hold } }, hold ? 'Returned · a new hold placed' : 'Returned before finishing');
      if (!back) return setBusy(false);
    }
    let ok: boolean;
    if (choice === 'keep_paused') {
      const paused = openRead(book)?.pauses.some((x) => x.paused_on && !x.resumed_on);
      ok = paused ? true : await edits.act('read_pause', {}, 'Paused for now');
      if (ok && (chips.length || reason.trim())) ok = await edits.act('read_pause_reason', { chips, reason: reason.trim() || null }, 'Paused for now · reason saved');
    } else {
      const details: Record<string, unknown> = { chips };
      if (reason.trim()) details.reason = reason.trim();
      if (choice === 'not_for_me') details.kind = kind;
      else details.place = place;
      ok = await edits.act('read_stop', { choice, details }, `Stopped ${book.work.title}`);
    }
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet
      title={loanCopy ? `Didn’t finish ${book.work.title}?` : `Stop reading ${book.work.title}?`}
      sub={`The read is kept in its history as stopped${pct !== null ? `, at ${Math.round(pct)}%` : ''}, with the date and why. Pause instead if you’re still reading it.`}
      onClose={onClose}
    >
      {tries > 0 ? <div class="heads-up">You’ve stopped this book {tries === 1 ? 'once' : `${tries} times`} before.</div> : null}
      <div class="bk-choices" role="radiogroup" aria-label="Where it goes">
        {choices.map((c) => (
          <button key={c.key} type="button" role="radio" aria-checked={choice === c.key} class={`bk-choice${choice === c.key ? ' on' : ''}`} onClick={() => pick(c.key)}>
            <strong>{c.name}</strong>
            <span>{c.note}</span>
          </button>
        ))}
      </div>
      {choice === 'keep_paused' ? null : choice === 'not_for_me' ? (
        <div class="bk-dialog-kind">
          Counts as <strong>{kind === 'abandoned' ? 'Abandoned' : 'Passed on'}</strong>
          <button type="button" class="btn-link" onClick={() => setKind(kind === 'abandoned' ? 'passed_on' : 'abandoned')}>
            Change
          </button>
        </div>
      ) : (
        <PlaceChoice shelf={choice} kept={book.keptShelves.includes(choice)} value={place} onChange={setPlace} />
      )}
      <ToggleChips class="bk-dialog-chips" label="Reasons" choices={STOP_CHIPS} on={chips} onChange={setChips} small={false} />
      <label class="bk-field">
        <span class="bk-label">Why, in your words</span>
        <input type="text" value={reason} maxLength={2000} placeholder="Optional · only you see this" onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
      </label>
      {loanCopy ? (
        <label class="bk-check">
          <input type="checkbox" checked={hold} onChange={(e) => setHold((e.target as HTMLInputElement).checked)} />
          Place a new hold
        </label>
      ) : null}
      <div class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy} onClick={() => void save()}>
          {choice === 'keep_paused' ? 'Return it · keep the read paused' : choice === 'not_for_me' ? 'Move to Not for me' : 'Stop and mark Try again'}
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

// ---------- stars ----------

/** Stars in halves: tap a star for whole, the same star again for a half, a third time to clear. */
/** `hint`: the visible "tap again for a half" line (the Finish sheet; My reads has it as a tooltip). */
export function Stars({ value, onChange, label = 'Your rating', hint = false }: { value: number | null; onChange: (v: number | null) => void; label?: string; hint?: boolean }) {
  const next = (n: number) => (value === n ? n - 0.5 : value === n - 0.5 ? null : n);
  return (
    <span class="bk-stars" role="group" aria-label={label} title="Tap a star again for a half; a third time clears it.">
      {[1, 2, 3, 4, 5].map((n) => {
        const fill = value === null ? 0 : value >= n ? 1 : value >= n - 0.5 ? 0.5 : 0;
        return (
          <button
            key={n}
            type="button"
            class={`bk-star${fill === 1 ? ' on' : fill === 0.5 ? ' half' : ''}`}
            aria-label={`${n} ${n === 1 ? 'star' : 'stars'}${value === n ? ' (tap again for a half)' : ''}`}
            aria-pressed={fill > 0}
            onClick={() => onChange(next(n))}
          >
            ★
          </button>
        );
      })}
      <span class="bk-quiet">{value === null ? 'Not rated' : `★${value}`}</span>
      {hint ? <span class="bk-stars-hint">Tap a star again for a half; a third time clears it.</span> : null}
    </span>
  );
}

// ---------- Mark finished ----------

export function FinishSheet({ book, edits, onClose }: { book: BookData; edits: BookEdits; onClose: () => void }) {
  const [when, setWhen] = useState<'today' | 'other'>('today');
  const [date, setDate] = useState('');
  const [precision, setPrecision] = useState<'day' | 'month' | 'year' | 'about'>('day');
  const [words, setWords] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [again, setAgain] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const ok = await edits.act(
      'read_finish',
      {
        finished_on: when === 'today' ? null : date || null,
        finished_precision: when === 'today' ? 'day' : precision,
        words: words.trim() || null,
        rating,
        read_again: again,
      },
      `Finished ${book.work.title}${rating ? ` · ★${rating}` : ''}`,
    );
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet title={`Mark ${book.work.title} finished`} sub={book.tryAgainAt ? 'Marking it finished takes off the ↺ Try again mark.' : undefined} onClose={onClose}>
      <div class="bk-dialog-chips" role="group" aria-label="When">
        <Chip label="Today" on={when === 'today'} onToggle={() => setWhen('today')} />
        <Chip label="Another date" on={when === 'other'} onToggle={() => setWhen('other')} />
      </div>
      {when === 'other' ? (
        <div class="bk-when">
          <input type="date" aria-label="Finished on" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
          {/* A few choices inside a sheet are small chips, not a menu (R4, Kristi Nov 1). */}
          <ChoiceChips
            class="bk-when-chips"
            label="How exact"
            small
            choices={
              [
                ['day', 'That day'],
                ['month', 'That month'],
                ['year', 'That year'],
                ['about', 'About then'],
              ] as const
            }
            value={precision}
            onPick={(k) => k && setPrecision(k)}
          />
          <input type="text" aria-label="In your words" placeholder="In your words (optional)" maxLength={500} value={words} onInput={(e) => setWords((e.target as HTMLInputElement).value)} />
        </div>
      ) : null}
      <Stars value={rating} onChange={setRating} hint />
      <label class="bk-check">
        <input type="checkbox" checked={again} onChange={(e) => setAgain((e.target as HTMLInputElement).checked)} />
        Read again in future?
      </label>
      <div class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy || (when === 'other' && !date)} onClick={() => void save()}>
          Mark finished
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

// ---------- progress ----------

const pagesOf = (book: BookData, read: Read) => {
  const c = copiesInUse(book, read).find((x) => !isListen(x, editionOf(book, x)));
  return c ? (c.pages ?? editionOf(book, c)?.pages ?? null) : null;
};
const minutesOf = (book: BookData, read: Read) => {
  const c = copiesInUse(book, read).find((x) => isListen(x, editionOf(book, x)));
  return c ? (c.listening_minutes ?? editionOf(book, c)?.listening_minutes ?? null) : null;
};

/** h:mm or minutes → minutes. */
export function minutesFrom(text: string): number | null {
  const t = text.trim();
  const hm = /^(\d+)\s*[:h]\s*(\d{1,2})\s*m?$/i.exec(t);
  if (hm) return Number(hm[1]) * 60 + Number(hm[2]);
  if (/^\d+$/.test(t)) return Number(t);
  return null;
}

/** The slider (Kristi, Oct 27): drag, then Save; or enter a page or an Audible time. At 100%, Mark as finished? */
export function ProgressSlider({ book, read, edits, onFinish }: { book: BookData; read: Read; edits: BookEdits; onFinish: () => void }) {
  const now = latestProgress(read) ?? 0;
  const [value, setValue] = useState(Math.round(now));
  const [exact, setExact] = useState(false);
  const [page, setPage] = useState('');
  const [time, setTime] = useState('');
  const [asking, setAsking] = useState(false);
  const pages = pagesOf(book, read);
  const minutes = minutesOf(book, read);
  const changed = value !== Math.round(now);
  const save = async (pct: number, entered?: { value: number; unit: 'page' | 'minutes' }) => {
    if (pct >= 100 && !asking) {
      setAsking(true);
      return;
    }
    const ok = await edits.act('read_progress', { percent: pct, entered_value: entered?.value ?? null, entered_unit: entered?.unit ?? 'percent' }, `Progress ${Math.round(pct)}%`);
    if (ok) {
      setExact(false);
      setAsking(false);
    }
  };
  const fromExact = (): { pct: number; entered: { value: number; unit: 'page' | 'minutes' } } | null => {
    if (page && pages) {
      const p = Number(page);
      return p > 0 ? { pct: Math.min(100, (p / pages) * 100), entered: { value: p, unit: 'page' } } : null;
    }
    const m = minutesFrom(time);
    if (m !== null && minutes) return { pct: Math.min(100, (m / minutes) * 100), entered: { value: m, unit: 'minutes' } };
    return null;
  };
  return (
    <div class="bk-slider">
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        aria-label="Progress"
        aria-valuetext={`${value}%`}
        onInput={(e) => setValue(Number((e.target as HTMLInputElement).value))}
      />
      <div class="bk-slider-row">
        {changed ? (
          <button type="button" class="btn-main" onClick={() => void save(value)}>
            Save {value}%
          </button>
        ) : null}
        <button type="button" class="btn-link" aria-expanded={exact} onClick={() => setExact(!exact)}>
          or enter a page / time
        </button>
      </div>
      {exact ? (
        <div class="bk-when">
          {pages ? (
            <label class="bk-field-inline">
              Page
              <input type="number" min={1} max={pages} inputMode="numeric" value={page} onInput={(e) => setPage((e.target as HTMLInputElement).value)} />
              <span class="bk-quiet">of {pages}</span>
            </label>
          ) : null}
          {minutes ? (
            <label class="bk-field-inline">
              Time
              <input type="text" inputMode="numeric" placeholder="h:mm" value={time} onInput={(e) => setTime((e.target as HTMLInputElement).value)} />
              <span class="bk-quiet">of {timeText(minutes)}</span>
            </label>
          ) : null}
          {!pages && !minutes ? <span class="bk-quiet">No page count or length on this copy yet; use the slider.</span> : null}
          {pages || minutes ? (
            <button
              type="button"
              class="btn-secondary"
              disabled={!fromExact()}
              onClick={() => {
                const x = fromExact();
                if (x) void save(x.pct, x.entered);
              }}
            >
              Save
            </button>
          ) : null}
        </div>
      ) : null}
      {asking ? (
        <div class="heads-up bk-ask" role="alertdialog" aria-label="Mark as finished?">
          Mark as finished?
          <button type="button" class="btn-main" onClick={() => { setAsking(false); onFinish(); }}>
            Yes
          </button>
          <button type="button" class="btn-secondary" onClick={() => void save(100)}>
            No, just 100%
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ---------- pause reason, notes, quotes ----------

const PAUSE_CHIPS = ['Busy for now', 'Travelling', 'Reading something else first', 'Waiting on the library'];

export function PauseReason({ book, edits, existing }: { book: BookData; edits: BookEdits; existing: { chips: string[]; reason: string | null } }) {
  const [open, setOpen] = useState(false);
  const [chips, setChips] = useState(existing.chips);
  const [reason, setReason] = useState(existing.reason ?? '');
  if (!open) {
    const shown = [...existing.chips, existing.reason].filter(Boolean).join(' · ');
    return (
      <span class="bk-pause-reason">
        {shown ? <span class="bk-quiet">{shown}</span> : null}
        <button type="button" class="btn-link" onClick={() => setOpen(true)}>
          {shown ? 'change why' : 'add a reason'}
        </button>
      </span>
    );
  }
  return (
    <div class="bk-inplace editing">
      <ToggleChips class="bk-dialog-chips" label="Why it’s paused" choices={PAUSE_CHIPS} on={chips} onChange={setChips} small={false} />
      <input type="text" aria-label="Why it’s paused, in your words" placeholder="In your words (optional) · only you see this" maxLength={2000} value={reason} onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
      <span class="bk-buttons">
        <button
          type="button"
          class="btn-main"
          onClick={async () => {
            if (await edits.act('read_pause_reason', { chips, reason: reason.trim() || null }, `Saved why ${book.work.title} is paused`)) setOpen(false);
          }}
        >
          Save
        </button>
        <button type="button" class="btn-secondary" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </span>
    </div>
  );
}

/** + Reading note or + Add a quote, opened in place. */
export function AddEntry({ kind, edits, onClose }: { kind: 'note' | 'quote'; book?: BookData; edits: BookEdits; onClose: () => void }) {
  const [body, setBody] = useState('');
  const [location, setLocation] = useState('');
  const [chapter, setChapter] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const ok =
      kind === 'note'
        ? await edits.act('note_add', { body, location: location || null }, 'Reading note added')
        : await edits.act('quote_add', { body, location: location || null, chapter: chapter || null }, 'Quote added');
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <div class="bk-inplace editing bk-add-entry">
      <textarea
        aria-label={kind === 'note' ? 'Reading note' : 'Quote'}
        rows={3}
        maxLength={20000}
        placeholder={kind === 'note' ? 'How it’s going, what struck you… (dated today)' : 'The passage'}
        value={body}
        onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
        autoFocus
      />
      <div class="bk-when">
        <input type="text" aria-label="Where in the book" placeholder="Page or location (optional)" maxLength={100} value={location} onInput={(e) => setLocation((e.target as HTMLInputElement).value)} />
        {kind === 'quote' ? <input type="text" aria-label="Chapter" placeholder="Chapter (optional)" maxLength={300} value={chapter} onInput={(e) => setChapter((e.target as HTMLInputElement).value)} /> : null}
      </div>
      <span class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy || !body.trim()} onClick={() => void save()}>
          Save
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <span class="bk-quiet">Only you see this.</span>
      </span>
    </div>
  );
}

/** Where a move to Up Next or Future reads puts the book (Kristi, Oct 27: the same place picker). */
export function MovePlaceSheet({ book, to, edits, onClose }: { book: BookData; to: 'up_next' | 'future_reads'; edits: BookEdits; onClose: () => void }) {
  const [place, setPlace] = useState<Place>(defaultPlace(book, to));
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title={`Move ${book.work.title} to ${statusName(to)}`} onClose={onClose}>
      <PlaceChoice shelf={to} kept={book.keptShelves.includes(to)} value={place} onChange={setPlace} />
      <div class="bk-buttons">
        <button
          type="button"
          class="btn-main"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            const ok = await edits.move(to, { place });
            setBusy(false);
            if (ok) onClose();
          }}
        >
          Move
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}
